import { createApp, h, ref, type App } from 'vue';
import type { PlgPlanSDKOpenOptions, PlgPlanSDKOptions } from './src/types';
import type { I18n } from 'vue-i18n';
import { getCurrentLanguage } from '@eureka/language-tool';
import { cn, en, jp, tw } from './src/locales'
import _ from 'lodash';

export { default } from './src/PlgPlanManager.vue'

export class PlgPlanSDK {
    private _div: HTMLDivElement | null = null;
    private _html: HTMLHtmlElement | null = null;
    private _app: App<Element> | null = null;
    private i18n: I18n | undefined = undefined;

    constructor(options?: PlgPlanSDKOptions | undefined) {
        if (options?.i18n) {
            this.setI18n(options.i18n)
        }

        return this
    }

    private checkI18n() {
        if (!this.i18n) {
            throw new Error('i18n is required')
        }
    }

    getI18n() {
        return this.i18n
    }

    setI18n(i18n: I18n) {
        this.i18n = i18n
        const locale = getCurrentLanguage()
        const messages = {
            cn,
            en,
            jp,
            tw,
        }
        this.i18n.global.mergeLocaleMessage(locale, _.get(messages, locale))
        return this
    }

    private _createContainer() {
        this._html = window.parent.document.querySelector('html')
        this._html?.setAttribute('style', 'overflow:hidden')
        if (this._div) {
            this._div.remove()
        }
        this._div = document.createElement('div');
        this._div.setAttribute('data-theme', 'patsnapRD');
        // 设置容器为固定定位并置于最顶层，确保弹框不被其他元素遮挡
        this._div.setAttribute('style', 'position: fixed; top: 0; left: 0; width: 100%; height: 100%; z-index: 99999; pointer-events: none;');
        document.body.appendChild(this._div);
        return this._div;
    }

    private _destroyContainer() {
        this._div?.remove();
        this._app?.unmount();
        this._html?.removeAttribute('style');
        return this;
    }

    async openPlgPlanDialog(options?: PlgPlanSDKOpenOptions) {
        this.checkI18n();
        const div = this._createContainer();
        const component = await import('./src/components/SelectPlanDialog.vue').then(m => m.default)
        const showSuccessDialog = ref(false)

        const promise = new Promise((resolve) => {
            const $vm = this;
            this._app = createApp({
                render() {
                    return h(component, {
                        modelValue: showSuccessDialog.value,
                        success_url: options?.successUrl,
                        cancel_url: options?.cancelUrl,
                        view_more_url: options?.viewMoreUrl,
                        onClose: () => {
                            showSuccessDialog.value = false
                            resolve({ close: true });
                            $vm.close()
                        },
                    });
                },
            });
            this._app.use(this.i18n!)
            this._app.mount(div);
        });
        showSuccessDialog.value = true
        return promise;
    }

    async openSuccessDialog() {
        this.checkI18n();
        const div = this._createContainer();
        const component = await import('./src/components/SuccessDialog.vue').then(m => m.default)
        const showSuccessDialog = ref(false)

        const promise = new Promise((resolve) => {
            const $vm = this;
            this._app = createApp({
                render() {
                    return h(component, {
                        modelValue: showSuccessDialog.value,
                        onClose: () => {
                            showSuccessDialog.value = false
                            resolve({ success: true });
                            $vm.close()
                        },
                    });
                },
            });
            this._app.use(this.i18n!)
            this._app.mount(div);
        });
        showSuccessDialog.value = true
        return promise;
    }

    close() {
        this._destroyContainer();
    }
}

export const plgPlanSDK = new PlgPlanSDK();
